import { useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GenericResponseStatus,
  useExportExamEventExamineeScoreReportsLazyQuery,
} from '@exam-center/graphql';
import { Button, FileUploadOutlinedIcon, Flex, PaperSection } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';

import { ExportConfirmationModal } from '../../components';
import { ExamEventScoreReportProvider } from '../../providers';
import { useExamEventScoreReport } from '../../providers/exam-event-score-report-provider/use-exam-event-score-report';
import { ExamEventScoreReportFilterSection } from '../exam-event-score-report-filter-section';
import { ExamEventScoreReportScoreLegendSection } from '../exam-event-score-report-score-legend-section';
import { ExamEventScoreReportSummarySection } from '../exam-event-score-report-summary-section';
import { ExamEventScoreReportTable } from '../exam-event-score-report-table';

function ExportButton() {
  const { examEvent } = useExamEventDetails();
  const {
    scoreReports,
    selectedExamCourseId,
    selectedCourseLabel,
    selectedTagCategory,
    hasFilters,
  } = useExamEventScoreReport();
  const [isOpen, { open, close }] = useDisclosure();
  const { showSnackbar } = useSnackbar();
  const [exportScoreReports, { loading: isExporting }] =
    useExportExamEventExamineeScoreReportsLazyQuery();

  const handleExport = async () => {
    if (!selectedTagCategory) return;

    await exportScoreReports({
      variables: {
        exportExamEventExamineeScoreReportsInput: {
          examEventId: examEvent.id,
          examCourseId: selectedExamCourseId,
          tagCategory: selectedTagCategory,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data.ecExportExamEventExamineeScoreReports.status === GenericResponseStatus.Success) {
          showSnackbar({
            message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
            variant: 'success',
          });
        } else {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
        }
        close();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        close();
      },
    });
  };

  const isDisabled = !hasFilters || scoreReports.length === 0;

  return (
    <>
      <Button
        startIcon={<FileUploadOutlinedIcon />}
        disabled={isDisabled}
        variant='outlined'
        color='secondary'
        onClick={open}
      >
        Export
      </Button>

      {selectedTagCategory && (
        <ExportConfirmationModal
          open={isOpen}
          onClose={close}
          onConfirm={handleExport}
          examStartedAt={examEvent.examStartedAt}
          examEndedAt={examEvent.examEndedAt}
          examRound={examEvent.roundNumber}
          examType={examEvent.examType}
          examCode={selectedCourseLabel}
          category={selectedTagCategory}
          totalRecords={scoreReports.length}
          loading={isExporting}
        />
      )}
    </>
  );
}

export default function ExamEventScoreReportSection() {
  return (
    <ExamEventScoreReportProvider>
      <PaperSection
        title='รายงานสรุปผลการสอบ'
        endAdornment={<ExportButton />}
        sx={(theme) => ({
          borderRadius: theme.shape.rounded(2),
        })}
      >
        <Flex
          sx={(theme) => ({
            p: theme.spacing(6),
            gap: theme.spacing(6),
            flexDirection: 'column',
          })}
        >
          <ExamEventScoreReportFilterSection />
          <ExamEventScoreReportSummarySection />
          <ExamEventScoreReportScoreLegendSection />
          <ExamEventScoreReportTable />
        </Flex>
      </PaperSection>
    </ExamEventScoreReportProvider>
  );
}
